import { ForwardedRef, forwardRef, useEffect } from 'react';

import {
  DrawerProps,
  Tab as DsTab,
  TabContent as DsTabContent,
  TabContentProps,
  TabProps,
  Tabs,
  TabsProvider,
  Drawer,
  Typography,
  Box,
  tokens,
} from '@cast/design-system';
import { BestPracticeCheckStandard, CheckResourceStatus } from '@cast/types';
import { clearQueryParamSilently, useSearchParamState } from '@cast/utils';

import { DrawerHeader } from 'components/drawer';
import { DrawerContent } from 'components/drawer/ds';
import { useBestPracticeCheckQuery } from 'hooks/queries/security-insights';

import { Resources } from './_components';
import { CheckActions } from './_components/CheckActions';
import { TabValue } from './types';
import { CheckOverview } from '../../../_components/CheckOverview';
import { checkDetailsDrawerUrlKey } from '../../../constants';

import { FailedToLoad } from '@cast/shared-messages-ui';

const activeTabQueryParamKey = 'checks-drawer-tab';

const Tab = (props: TabProps) => {
  return <DsTab {...props} />;
};

const TabContent = (props: TabContentProps<TabValue>) => (
  <DsTabContent {...props} />
);

export type CheckDetailsDrawerPayload = {
  ruleId: string;
  tab?: TabValue;
  clusterIds?: string[];
  selectedClusters?: string[];
  selectedNamespaces?: string[];
  selectedLabels?: string[];
  selectedStandard?: BestPracticeCheckStandard;
  onAction?: () => void;
  onClose?: () => void;
};

type CheckDetailsDrawerProps = Omit<DrawerProps, 'onClose'> &
  CheckDetailsDrawerPayload;

export const CheckDetailsDrawer = forwardRef<any, CheckDetailsDrawerProps>(
  (
    {
      ruleId,
      tab = TabValue.OVERVIEW,
      clusterIds,
      selectedClusters,
      selectedNamespaces,
      selectedLabels,
      selectedStandard,
      ...drawerProps
    }: CheckDetailsDrawerProps,
    ref: ForwardedRef<any>
  ) => {
    const { check, error, refetch } = useBestPracticeCheckQuery({
      ruleId,
      standard: selectedStandard,
    });

    const { state: currentTab, setState: setCurrentTab } =
      useSearchParamState<TabValue>(activeTabQueryParamKey, tab);

    useEffect(() => {
      return () => {
        drawerProps.onClose?.();
        clearQueryParamSilently(activeTabQueryParamKey);
        clearQueryParamSilently(checkDetailsDrawerUrlKey);
      };
      // Only run this effect when the drawer is closed
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (error) {
      return (
        <Drawer ref={ref} size="medium" {...drawerProps}>
          <Box display="flex" alignItems="center" px="40px" py="16px" mt="60px">
            <FailedToLoad title="Failed to load details" refresh={refetch} />
          </Box>
        </Drawer>
      );
    }

    return (
      <Drawer
        testId="check-details-drawer"
        ref={ref}
        size="xlarge"
        {...drawerProps}
      >
        <DrawerHeader
          noDivider
          sticky={false}
          actions={<CheckActions check={check} />}
          onClose={drawerProps.onClose}
        >
          <Typography
            variant="L12R"
            color={tokens.theme.fg.structural.secondary}
            mt={16}
          >
            Check
          </Typography>
          <Typography variant="h5" mb={16}>
            {check?.name}
          </Typography>
          <Typography
            variant="P14R"
            color={tokens.theme.fg.structural.secondary}
            mb={24}
          >
            {check?.description}
          </Typography>
        </DrawerHeader>
        <DrawerContent flexGrow={1}>
          <TabsProvider currentTab={currentTab} setCurrentTab={setCurrentTab}>
            <Tabs
              size="small"
              fullWidth
              sx={{ mb: 24 }}
              testId="check-details-drawer-tabs"
            >
              <Tab value={TabValue.OVERVIEW} label="Overview & details" />
              <Tab value={TabValue.RESOURCES} label="Resources" />
            </Tabs>
            <TabContent value={TabValue.OVERVIEW}>
              <CheckOverview check={check} sx={{ pt: 8 }} />
            </TabContent>
            <TabContent value={TabValue.RESOURCES}>
              <Resources
                ruleId={ruleId}
                clusterIds={clusterIds}
                selectedClusters={selectedClusters}
                selectedNamespaces={selectedNamespaces}
                selectedLabels={selectedLabels}
                selectedType={CheckResourceStatus.affected}
              />
            </TabContent>
          </TabsProvider>
        </DrawerContent>
      </Drawer>
    );
  }
);

CheckDetailsDrawer.displayName = 'CheckDetailsDrawer';
